import React from "react";
import { RoundTripFlight } from "../../models/RoundTripFlight";
import {Flight} from "../../models/Flight";
import { DepartureFlightIcon } from "../../assets/svgComponents/DepartureFlightIcon";
import { ReturnFlightIcon } from "../../assets/svgComponents/ReturnFlightIcon";
import { useTranslation } from "../../i18n/LanguageProvider";
import ShareFlightButton from "../buttons/ShareFlightButton";
import BookmarkFlightButton from "../shortlist/BookmarkFlightButton";

interface Props {
  roundTripFlight: RoundTripFlight;
  showShare?: boolean;
}

export const buildOneWayBookingLink = (flight: Flight) =>
  `https://www.ryanair.com/de/de/trip/flights/select?${new URLSearchParams({
    adults: "1", teens: "0", children: "0", infants: "0",
    dateOut: flight.departure_date, isConnectedFlight: "false", isReturn: "false",
    originIata: flight.departure_iata, destinationIata: flight.destination_iata,
  })}`;

const RoundTripFlightInfoBlock: React.FC<Props> = ({ roundTripFlight, showShare = false }) => {
  const { outbound_flight: outbound, inbound_flight: inbound, total_price, booking_link } = roundTripFlight;
  const {locale, t} = useTranslation();
  const outboundLegs = roundTripFlight.outbound_flights?.length
    ? roundTripFlight.outbound_flights
    : [outbound];
  const inboundLegs = roundTripFlight.inbound_flights?.length
    ? roundTripFlight.inbound_flights
    : inbound
      ? [inbound]
      : [];
  const hasConnection = outboundLegs.length > 1 || inboundLegs.length > 1;
  const isOneWay = !inbound;
  const connectionBookingLegs = [...outboundLegs, ...inboundLegs];
  const hasEstimatedPrice = connectionBookingLegs.some((leg) => leg.price_is_estimated);

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
  };

  const getDateTime = (date: string, time: string) => new Date(`${date}T${time}`);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) return `${remainingMinutes}m`;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getLayoverMinutes = (legs: typeof outboundLegs, firstLegIndex: number) => {
    const firstLeg = legs[firstLegIndex];
    const nextLeg = legs[firstLegIndex + 1];
    if (!firstLeg || !nextLeg) return null;

    const arrivalDate = firstLeg.arrival_date ?? firstLeg.departure_date;
    const layoverMs =
      getDateTime(nextLeg.departure_date, nextLeg.departure_time).getTime() -
      getDateTime(arrivalDate, firstLeg.arrival_time).getTime();

    if (layoverMs <= 0) return null;
    return Math.round(layoverMs / 60000);
  };

  const formattedDeparture = new Date(outboundLegs[0].departure_date).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
  });

  const formattedReturn = inboundLegs.length
    ? new Date(inboundLegs[0].departure_date).toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
      })
    : null;

  const dateRange = formattedReturn ? `${formattedDeparture} – ${formattedReturn}` : formattedDeparture;
  const formatLeg = (departureIata: string, destinationIata: string) => `${departureIata} → ${destinationIata}`;
  const currencySymbol = (currency: string) => {
    if (currency === "EUR") return "€";
    if (currency === "GBP") return "£";
    return ` ${currency}`;
  };
  const formatPrice = (price: number, currency: string, isEstimated = false) =>
    `${isEstimated ? `${t("flight.fromPrice")} ` : ""}${price.toFixed(0)}${currencySymbol(currency)}`;
  const formatEuroPrice = (price: number, isEstimated = false) =>
    `${isEstimated ? `${t("flight.fromPrice")} ` : ""}${price.toFixed(0)}€`;
  const priceCurrencies = Array.from(new Set(connectionBookingLegs.map((leg) => leg.currency)));
  const hasMixedCurrencies = hasConnection && priceCurrencies.length > 1;
  const displayPrice = formatEuroPrice(total_price, hasEstimatedPrice);
  const renderJourneyLegs = (
    legs: typeof outboundLegs,
    label: string,
    reverseIcon = false,
    centerContent = false
  ) => (
    <div className={`journey-legs flex min-w-0 sm:min-w-[176px] max-w-full sm:max-w-[230px] flex-col gap-2 ${centerContent ? "items-center text-center" : ""}`}>
      {label && (
        <span className="text-[10px] font-extrabold uppercase text-[#9E9E9E] xl:text-xs">
          {label}
        </span>
      )}
      {legs.map((leg, index) => {
        const layoverMinutes = getLayoverMinutes(legs, index);

        return (
          <React.Fragment key={`${label}-${leg.departure_iata}-${leg.destination_iata}-${leg.departure_date}-${leg.departure_time}`}>
            <div className={`flex min-w-0 flex-col ${centerContent ? "items-center" : ""}`}>
              <span className={`flex min-w-0 items-center gap-2 whitespace-nowrap text-sm font-extrabold tracking-tight text-primary xl:text-base ${centerContent ? "justify-center" : ""}`}>
                {reverseIcon ? <ReturnFlightIcon /> : <DepartureFlightIcon />}
                <span className="shrink-0">{formatLeg(leg.departure_iata, leg.destination_iata)}</span>
                <span className="shrink-0 whitespace-nowrap font-bold text-[#858585]">
                  {formatTime(leg.departure_time)}-{formatTime(leg.arrival_time)}
                </span>
              </span>
              <span title={leg.destination_airport} className="mt-0.5 w-full truncate text-xs font-bold text-[#BEBEBE] xl:text-sm">
                {index === 0 && legs.length > 1
                  ? `${t("connection.via", { airport: leg.destination_airport })}`
                  : leg.destination_airport}
              </span>
            </div>
            {layoverMinutes !== null && (
              <div className={`${centerContent ? "" : "ml-7"} w-fit rounded-full bg-[#F4F2FF] px-3 py-1 text-xs font-extrabold text-accent`}>
                {t("connection.waitTime", { duration: formatDuration(layoverMinutes) })}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="flight-info flex w-full min-w-0 flex-1 flex-col items-start justify-between gap-4 transition-smooth sm:flex-row sm:items-center sm:gap-5">
      {/* Left: Info Block */}
      <div className={`flight-journeys min-w-0 flex-1 ${hasConnection ? `flex flex-col sm:flex-row items-center gap-5 md:gap-7 xl:gap-9 ${isOneWay ? "justify-center" : ""}` : `grid items-center gap-4 lg:gap-6 xl:gap-8 ${inbound ? "sm:grid-cols-[max-content_minmax(0,1.3fr)_minmax(0,1fr)]" : "sm:grid-cols-[max-content_minmax(0,1fr)]"}`} w-full sm:w-auto transition-smooth`}>
        {/* Date range */}
        <div className={`flight-dates flex shrink-0 flex-col ${hasConnection ? "items-center text-center min-w-[86px]" : "text-start"}`}>
          <span className={`${hasConnection ? "text-lg xl:text-xl font-extrabold" : "text-sm xl:text-base font-bold"} whitespace-nowrap text-primary`}>
            {dateRange}
          </span>
          <span className={`${hasConnection ? "mt-1 text-xs" : "text-xs xl:text-sm"} font-bold text-[#BEBEBE]`}>
            {hasConnection ? `${t("connection.oneStop")} · ${t("connection.selfTransfer")}` : inbound ? t("flight.period") : t("flight.outbound")}
          </span>
        </div>

        {/* Outbound flight */}
        {hasConnection ? (
          <div className={`connection-journeys flex min-w-0 flex-1 flex-col flex-wrap gap-4 py-1 text-start lg:flex-row lg:gap-5 xl:gap-6 ${isOneWay ? "items-center justify-center text-center" : ""}`}>
            {renderJourneyLegs(outboundLegs, isOneWay ? "" : t("flight.outbound"), false, isOneWay)}
            {inboundLegs.length > 0 && renderJourneyLegs(inboundLegs, t("flight.inbound"), true)}
          </div>
        ) : (
          <div className="flex min-w-0 flex-col text-start">
            <span className="text-sm xl:text-base font-bold text-primary flex items-center gap-1.5 whitespace-nowrap [&>svg]:shrink-0">
              <DepartureFlightIcon />
              {formatTime(outbound.departure_time)} {t("flight.timeSuffix")}
            </span>
            <span title={outbound.departure_airport} className="truncate text-xs xl:text-sm font-bold text-[#BEBEBE]">{outbound.departure_airport}</span>
          </div>
        )}

        {/* Inbound flight */}
        {inbound && !hasConnection && (
        <div className="flex min-w-0 flex-col text-start">
          <span className="text-sm xl:text-base font-bold text-primary flex items-center gap-1.5 whitespace-nowrap [&>svg]:shrink-0">
            <ReturnFlightIcon />
            {formatTime(inbound.departure_time)} {t("flight.timeSuffix")}
          </span>
          <span title={inbound.departure_airport} className="truncate text-xs xl:text-sm font-bold text-[#BEBEBE]">{inbound.departure_airport}</span>
        </div>
        )}
      </div>

      {/* Right: Price + Button */}
<div className={`flight-price flex shrink-0 gap-2 self-end sm:self-center sm:gap-4 ${showShare ? "flex-row flex-wrap items-center justify-end" : "flex-col items-start sm:flex-row sm:items-center"}`}>
  <div className="flex shrink-0 items-center gap-2">
  {showShare && <><BookmarkFlightButton flight={roundTripFlight}/><ShareFlightButton flight={roundTripFlight} /></>}
  <details className="relative group price-breakdown">
    <summary className="cursor-pointer list-none bg-gray-400 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-sm lg:text-base font-bold rounded-xl whitespace-nowrap">
      {displayPrice}
    </summary>

    {/* Tooltip */}
    <span className="absolute z-10 left-1/2 -translate-x-1/2 -top-14 w-max px-3 py-2 rounded-lg bg-black text-white text-xs font-medium opacity-0 group-open:opacity-100 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
      {hasMixedCurrencies && <>{t("flight.mixedCurrencyHint")}<br /></>}
      {t("flight.outbound")}: {formatPrice(outbound.price, outbound.currency, outbound.price_is_estimated)} <br />
      {inbound
        ? <>{t("flight.inbound")}: {formatPrice(inbound.price, inbound.currency, inbound.price_is_estimated)}{hasEstimatedPrice && <><br />{t("flight.estimatedPriceHint")}</>}</>
        : hasConnection
          ? <>{t("connection.oneStop")} · {t("connection.selfTransfer")}{hasEstimatedPrice && <><br />{t("flight.estimatedPriceHint")}</>}</>
          : <>{t("flight.oneWay")}</>}
    </span>
  </details>
  </div>

  {hasConnection ? (
    <details className="relative connection-booking">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-center whitespace-nowrap rounded-full bg-[#712FFF] px-4 text-xs font-bold text-white hover:bg-[#5b23cc]">
        {t("flight.book")}
      </summary>
      <div className="absolute bottom-full right-0 z-20 mb-2 w-60 max-w-[75vw] rounded-2xl bg-white p-3 shadow-card ring-1 ring-[#F2F0FA]">
        <p className="mb-2 text-xs font-bold text-primary">{t("flight.bookLegs", {count: connectionBookingLegs.length})}</p>
        {connectionBookingLegs.map((leg, index) => (
          <a
            key={`${leg.departure_iata}-${leg.destination_iata}-${index}`}
            href={buildOneWayBookingLink(leg)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[44px] flex-col justify-center rounded-lg px-2 py-2 text-xs font-bold text-accent hover:bg-[#F4F2FF]"
          >
            <span>{formatLeg(leg.departure_iata, leg.destination_iata)}</span>
            <span className="text-primary">{new Date(leg.departure_date).toLocaleDateString(locale)} · {formatTime(leg.departure_time)}</span>
          </a>
        ))}
      </div>
    </details>
  ) : (
    <a
      href={booking_link}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-full bg-[#712FFF] px-4 py-1.5 text-xs font-bold text-white transition hover:bg-[#5b23cc] sm:py-2 lg:px-5 lg:text-sm"
    >
      {t("flight.book")}
    </a>
  )}
</div>

    </div>
  );
};

export default RoundTripFlightInfoBlock;
